import type { GameMode, MatchEvent, PlayerOutcome } from './types';

/** Every tunable XP number in one place. See docs/GAME_DESIGN.md for the rationale. */
export const XP_RULES = {
  hitBase: 10,
  /** Hit XP adds floor(damage / hitDamageDivisor). */
  hitDamageDivisor: 5,
  headshotBonus: 15,
  directHitBonus: 10,
  longShotBonus: 10,
  longShotMinFraction: 0.6,
  kill: 40,
  drownKillBonus: 10,
  /** Per kill beyond the first from a single shot. */
  multiKillExtra: 25,
  turnPlayed: 2,
  turnPlayedCap: 30,
  win: { versus: 100, brawl: 100, coop: 80, campaign: 60 } satisfies Record<GameMode, number>,
  loss: 25,
  lossMinTurns: 3,
  draw: 50,
  flawless: 75,
  domination: 50,
  dominationHpFraction: 0.6,
  quick: 40,
  comeback: 60,
  /** Per newly earned campaign star. */
  star: 20,
  dailyFirstWin: 50,
} as const;

export type XpReason =
  | 'hit'
  | 'headshot'
  | 'directHit'
  | 'longShot'
  | 'kill'
  | 'drownKill'
  | 'multiKill'
  | 'turnsPlayed'
  | 'win'
  | 'loss'
  | 'draw'
  | 'flawless'
  | 'domination'
  | 'quick'
  | 'comeback'
  | 'stars'
  | 'dailyFirstWin';

/** Display order on the results screen: events first, outcome, then the bonuses. */
export const XP_REASON_ORDER: readonly XpReason[] = [
  'hit',
  'headshot',
  'directHit',
  'longShot',
  'kill',
  'drownKill',
  'multiKill',
  'turnsPlayed',
  'win',
  'loss',
  'draw',
  'flawless',
  'domination',
  'quick',
  'comeback',
  'stars',
  'dailyFirstWin',
];

export const XP_REASON_LABELS: Record<XpReason, string> = {
  hit: 'Hits',
  headshot: 'Headshots',
  directHit: 'Direct hits',
  longShot: 'Long shots',
  kill: 'Kills',
  drownKill: 'Splash kills',
  multiKill: 'Multi-kills',
  turnsPlayed: 'Turns played',
  win: 'Victory',
  loss: 'Fought to the end',
  draw: 'Draw',
  flawless: 'Flawless',
  domination: 'Domination',
  quick: 'Quick win',
  comeback: 'Comeback',
  stars: 'New stars',
  dailyFirstWin: 'First win of the day',
};

export interface XpLine {
  reason: XpReason;
  amount: number;
  /** How many times the reason applied (hits, kills, extra kills, stars…). 1 for one-offs. */
  count: number;
}

export interface XpBreakdown {
  lines: XpLine[];
  total: number;
}

function addLine(lines: Map<XpReason, XpLine>, reason: XpReason, amount: number, count = 1): void {
  if (amount <= 0 || count <= 0) return;
  const existing = lines.get(reason);
  if (existing) {
    existing.amount += amount;
    existing.count += count;
  } else {
    lines.set(reason, { reason, amount, count });
  }
}

function finish(lines: Map<XpReason, XpLine>): XpBreakdown {
  const ordered = XP_REASON_ORDER.filter((r) => lines.has(r)).map((r) => lines.get(r)!);
  return { lines: ordered, total: ordered.reduce((sum, l) => sum + l.amount, 0) };
}

export function turnsPlayedBy(playerId: string, events: readonly MatchEvent[]): number {
  return events.filter((e) => e.type === 'turnPlayed' && e.actor === playerId).length;
}

/** XP from the things the player did during the match. Friendly fire and self-damage earn nothing. */
export function scoreEvents(playerId: string, events: readonly MatchEvent[]): XpBreakdown {
  const lines = new Map<XpReason, XpLine>();
  const killsPerShot = new Map<string, number>();
  let turns = 0;

  for (const e of events) {
    if (e.actor !== playerId) continue;
    switch (e.type) {
      case 'hit': {
        if (e.relation !== 'enemy' || e.damage <= 0) break;
        addLine(lines, 'hit', XP_RULES.hitBase + Math.floor(e.damage / XP_RULES.hitDamageDivisor));
        if (e.headshot) addLine(lines, 'headshot', XP_RULES.headshotBonus);
        if (e.direct) addLine(lines, 'directHit', XP_RULES.directHitBonus);
        if (e.distanceFraction >= XP_RULES.longShotMinFraction) {
          addLine(lines, 'longShot', XP_RULES.longShotBonus);
        }
        break;
      }
      case 'kill': {
        if (e.relation !== 'enemy') break;
        addLine(lines, 'kill', XP_RULES.kill);
        if (e.cause === 'drown') addLine(lines, 'drownKill', XP_RULES.drownKillBonus);
        killsPerShot.set(e.shotId, (killsPerShot.get(e.shotId) ?? 0) + 1);
        break;
      }
      case 'turnPlayed':
        turns++;
        break;
    }
  }

  for (const kills of killsPerShot.values()) {
    if (kills >= 2) addLine(lines, 'multiKill', (kills - 1) * XP_RULES.multiKillExtra, kills - 1);
  }
  if (turns > 0) {
    addLine(lines, 'turnsPlayed', Math.min(turns * XP_RULES.turnPlayed, XP_RULES.turnPlayedCap), turns);
  }
  return finish(lines);
}

export interface OutcomeContext {
  /** Needed for the loss consolation, which only pays if the player actually played. */
  turnsPlayed: number;
}

/** XP from how the match ended, including the strong-victory bonuses. */
export function scoreOutcome(outcome: PlayerOutcome, mode: GameMode, ctx: OutcomeContext): XpBreakdown {
  const lines = new Map<XpReason, XpLine>();
  switch (outcome.result) {
    case 'win': {
      addLine(lines, 'win', XP_RULES.win[mode]);
      if (isFlawless(outcome)) addLine(lines, 'flawless', XP_RULES.flawless);
      if (isDomination(outcome)) addLine(lines, 'domination', XP_RULES.domination);
      if (isQuick(outcome)) addLine(lines, 'quick', XP_RULES.quick);
      if (outcome.lastStand) addLine(lines, 'comeback', XP_RULES.comeback);
      break;
    }
    case 'loss':
      if (ctx.turnsPlayed >= XP_RULES.lossMinTurns) addLine(lines, 'loss', XP_RULES.loss);
      break;
    case 'draw':
      addLine(lines, 'draw', XP_RULES.draw);
      break;
  }
  return finish(lines);
}

export function isFlawless(o: PlayerOutcome): boolean {
  return o.result === 'win' && o.unitsStart > 0 && o.unitsAlive >= o.unitsStart;
}

export function isDomination(o: PlayerOutcome): boolean {
  return o.result === 'win' && o.hpStart > 0 && o.hpRemaining / o.hpStart >= XP_RULES.dominationHpFraction;
}

export function isQuick(o: PlayerOutcome): boolean {
  return o.result === 'win' && o.parRounds > 0 && o.roundsPlayed <= o.parRounds;
}

/** Campaign star rating for an outcome: cleared / within par / flawless. */
export function starsForOutcome(o: PlayerOutcome): 0 | 1 | 2 | 3 {
  if (o.result !== 'win') return 0;
  let stars = 1;
  if (isQuick(o)) stars++;
  if (isFlawless(o)) stars++;
  return stars as 1 | 2 | 3;
}

/** Everything a match pays one player, before profile-level extras (stars, daily bonus). */
export function scoreMatch(
  playerId: string,
  events: readonly MatchEvent[],
  outcome: PlayerOutcome,
  mode: GameMode,
): XpBreakdown {
  const fromEvents = scoreEvents(playerId, events);
  const fromOutcome = scoreOutcome(outcome, mode, { turnsPlayed: turnsPlayedBy(playerId, events) });
  return mergeBreakdowns(fromEvents, fromOutcome);
}

export function mergeBreakdowns(...parts: XpBreakdown[]): XpBreakdown {
  const lines = new Map<XpReason, XpLine>();
  for (const part of parts) for (const l of part.lines) addLine(lines, l.reason, l.amount, l.count);
  return finish(lines);
}

export function starsLine(newStars: number): XpBreakdown {
  const lines = new Map<XpReason, XpLine>();
  addLine(lines, 'stars', newStars * XP_RULES.star, newStars);
  return finish(lines);
}

export function dailyFirstWinLine(): XpBreakdown {
  const lines = new Map<XpReason, XpLine>();
  addLine(lines, 'dailyFirstWin', XP_RULES.dailyFirstWin);
  return finish(lines);
}
